import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import {
  Activity, Search, RefreshCw, ChevronLeft, ChevronRight, Calendar,
  Filter, FileText, AlertCircle, User as UserIcon, ShieldCheck,
} from 'lucide-react';
import AuditHeatmap from './AuditHeatmap';

const ACTION_GROUPS = [
  { id: 'all', label: 'All actions' },
  { id: 'login_success', label: 'Logins (success)' },
  { id: 'login_failed', label: 'Logins (failed)' },
  { id: 'user_register', label: 'New registrations' },
  { id: 'password_reset', label: 'Password resets' },
  { id: 'job_create', label: 'Jobs posted' },
  { id: 'job_archive', label: 'Jobs archived' },
  { id: 'job_delete', label: 'Jobs deleted' },
  { id: 'job_delete_cutoff_blocked', label: 'Delete blocked (cutoff)' },
  { id: 'job_restore', label: 'Jobs restored' },
  { id: 'admin_delete_user', label: 'Admin: user deleted' },
  { id: 'admin_bulk_delete_users', label: 'Admin: bulk delete' },
  { id: 'admin_archive_user', label: 'Admin: user archived' },
  { id: 'admin_bulk_archive_users', label: 'Admin: bulk archive' },
  { id: 'admin_wipe_shifts', label: 'Admin: wipe shifts' },
  { id: 'password_confirm_failed', label: 'Password confirm failed' },
];

const ROLE_OPTIONS = [
  { id: 'all', label: 'All roles' },
  { id: 'admin', label: 'Admin' },
  { id: 'employer', label: 'Employer' },
  { id: 'agent', label: 'Agent' },
  { id: 'worker', label: 'Worker' },
];

const STATUS_COLORS = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failure: 'bg-red-50 text-red-700 border-red-200',
  blocked: 'bg-amber-50 text-amber-700 border-amber-200',
};

const ROLE_COLORS = {
  admin: 'bg-violet-50 text-violet-700 border-violet-200',
  employer: 'bg-blue-50 text-blue-700 border-blue-200',
  agent: 'bg-teal-50 text-teal-700 border-teal-200',
  worker: 'bg-amber-50 text-amber-700 border-amber-200',
};

const ACTION_LABELS = {
  login_success: 'Login',
  login_failed: 'Login failed',
  user_register: 'Registration',
  user_register_failed: 'Registration failed',
  password_reset: 'Password reset',
  password_confirm_failed: 'Password confirm failed',
  job_create: 'Job posted',
  job_archive: 'Job archived',
  job_delete: 'Job deleted',
  job_delete_cutoff_blocked: 'Delete blocked (cutoff)',
  job_restore: 'Job restored',
  admin_delete_user: 'User deleted',
  admin_archive_user: 'User archived',
  admin_bulk_delete_users: 'Bulk user delete',
  admin_bulk_archive_users: 'Bulk user archive',
  admin_wipe_shifts: 'Shifts wiped',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString([], {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

const PAGE_SIZE = 50;

export default function AuditTrailPanel() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const [action, setAction] = useState('all');
  const [actorRole, setActorRole] = useState('all');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (action && action !== 'all') params.set('action', action);
      if (actorRole && actorRole !== 'all') params.set('actor_role', actorRole);
      if (q) params.set('q', q);
      if (dateFrom) params.set('date_from', new Date(dateFrom).toISOString());
      if (dateTo) {
        const t = new Date(dateTo);
        t.setHours(23, 59, 59, 999);
        params.set('date_to', t.toISOString());
      }
      params.set('skip', String(skip));
      params.set('limit', String(PAGE_SIZE));
      const res = await apiCall(`/api/admin/audit-logs?${params.toString()}`);
      setLogs(res.logs || []);
      setTotal(res.total || 0);
    } catch (e) {
      toast.error(e?.message || 'Failed to load audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [action, actorRole, q, dateFrom, dateTo, skip]);

  const loadStats = useCallback(async () => {
    try {
      const res = await apiCall('/api/admin/audit-logs/stats');
      setStats(res);
    } catch {
      setStats(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const onSearch = (e) => {
    e?.preventDefault?.();
    setSkip(0);
    setQ(qInput.trim());
  };

  const resetFilters = () => {
    setAction('all');
    setActorRole('all');
    setQ('');
    setQInput('');
    setDateFrom('');
    setDateTo('');
    setSkip(0);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="space-y-6" data-testid="audit-trail-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-violet-600" />
            Audit Trail
          </h2>
          <p className="text-slate-500 mt-1">
            Every sensitive action across workers, employers, agents and admins is recorded here.
          </p>
        </div>
        <button
          onClick={() => { load(); loadStats(); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 font-medium hover:bg-slate-50 transition"
          data-testid="audit-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Heatmap */}
      <AuditHeatmap
        onSelectDate={(d) => {
          setDateFrom(d);
          setDateTo(d);
          setSkip(0);
        }}
      />

      {/* Stats summary */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Total events" value={stats.total ?? 0} icon={<FileText className="w-4 h-4" />} tone="violet" />
          <StatCard label="Today" value={stats.today ?? 0} icon={<Calendar className="w-4 h-4" />} tone="emerald" />
          <StatCard
            label="Top action"
            value={stats.by_action?.[0]?.action || '—'}
            sub={stats.by_action?.[0]?.count ? `${stats.by_action[0].count} times` : ''}
            icon={<Activity className="w-4 h-4" />}
            tone="blue"
          />
          <StatCard
            label="Most active role"
            value={stats.by_role?.[0]?.role || '—'}
            sub={stats.by_role?.[0]?.count ? `${stats.by_role[0].count} events` : ''}
            icon={<UserIcon className="w-4 h-4" />}
            tone="amber"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Filter className="w-4 h-4" /> Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <select
            value={action}
            onChange={(e) => { setAction(e.target.value); setSkip(0); }}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400"
            data-testid="audit-filter-action"
          >
            {ACTION_GROUPS.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
          <select
            value={actorRole}
            onChange={(e) => { setActorRole(e.target.value); setSkip(0); }}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400"
            data-testid="audit-filter-role"
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setSkip(0); }}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400"
            data-testid="audit-filter-date-from"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setSkip(0); }}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400"
            data-testid="audit-filter-date-to"
          />
        </div>
        <form onSubmit={onSearch} className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search by email, name, action, or target id…"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400"
              data-testid="audit-search-input"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 transition"
            data-testid="audit-search-btn"
          >
            Search
          </button>
          <button
            type="button"
            onClick={resetFilters}
            className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition"
            data-testid="audit-reset-filters"
          >
            Reset
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">When</th>
                <th className="text-left px-4 py-3 font-semibold">Actor</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                <th className="text-left px-4 py-3 font-semibold">Action</th>
                <th className="text-left px-4 py-3 font-semibold">Target</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && logs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    No audit entries match these filters.
                  </td>
                </tr>
              )}
              {logs.map((log) => (
                <tr
                  key={log._id}
                  className="hover:bg-slate-50 cursor-pointer transition"
                  onClick={() => setSelectedLog(log)}
                  data-testid={`audit-row-${log._id}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{formatDate(log.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 truncate max-w-[200px]">
                      {log.actor_name || log.actor_email || '—'}
                    </div>
                    {log.actor_email && log.actor_name && (
                      <div className="text-xs text-slate-500 truncate max-w-[200px]">{log.actor_email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {log.actor_role && (
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${ROLE_COLORS[log.actor_role] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {log.actor_role}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700 font-medium">
                    {ACTION_LABELS[log.action] || log.action}
                  </td>
                  <td className="px-4 py-3 text-slate-600 max-w-[260px]">
                    {log.target_type && (
                      <div className="text-xs text-slate-400">{log.target_type}</div>
                    )}
                    <div className="truncate">
                      {log.target_summary?.title
                        || log.target_summary?.email
                        || log.target_summary?.full_name
                        || log.target_id
                        || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_COLORS[log.status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                      {log.status || 'success'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50 text-sm">
          <span className="text-slate-600">
            {total === 0 ? '0' : `${skip + 1}–${Math.min(skip + logs.length, total)}`} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              disabled={skip === 0 || loading}
              className="px-2 py-1.5 rounded-lg text-slate-600 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="audit-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2 text-slate-700 font-medium">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setSkip(skip + PAGE_SIZE)}
              disabled={skip + PAGE_SIZE >= total || loading}
              className="px-2 py-1.5 rounded-lg text-slate-600 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="audit-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      {selectedLog && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setSelectedLog(null)}
          data-testid="audit-detail-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-violet-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-slate-900">
                  {ACTION_LABELS[selectedLog.action] || selectedLog.action}
                </h3>
                <p className="text-xs text-slate-500">{formatDate(selectedLog.created_at)}</p>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-slate-400 hover:text-slate-700 text-xl"
                aria-label="Close"
                data-testid="audit-detail-close"
              >×</button>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm">
              <DetailRow label="Actor">
                {selectedLog.actor_name || '—'}
                {selectedLog.actor_email && <span className="text-slate-500 ml-2">({selectedLog.actor_email})</span>}
              </DetailRow>
              <DetailRow label="Role">{selectedLog.actor_role || '—'}</DetailRow>
              <DetailRow label="Status">{selectedLog.status || 'success'}</DetailRow>
              <DetailRow label="IP / Agent">
                <div className="text-slate-700">{selectedLog.ip_address || '—'}</div>
                <div className="text-xs text-slate-400 truncate">{selectedLog.user_agent || ''}</div>
              </DetailRow>
              <DetailRow label="Target">
                <div className="text-slate-700">{selectedLog.target_type || '—'} {selectedLog.target_id && (<span className="text-slate-400">· {selectedLog.target_id}</span>)}</div>
              </DetailRow>
              {selectedLog.target_summary && Object.keys(selectedLog.target_summary).length > 0 && (
                <DetailRow label="Target details">
                  <pre className="text-xs bg-slate-50 rounded p-3 text-slate-700 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(selectedLog.target_summary, null, 2)}
                  </pre>
                </DetailRow>
              )}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <DetailRow label="Metadata">
                  <pre className="text-xs bg-slate-50 rounded p-3 text-slate-700 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </DetailRow>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, icon, tone = 'violet' }) {
  const tones = {
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold border ${tones[tone]}`}>
        {icon}
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold text-slate-900 truncate">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-start">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="col-span-2 text-slate-800">{children}</div>
    </div>
  );
}
